import { memo, useState } from 'react';
import { TANO_ROBOT_FILTRES, TANO_ACCESSOIRE, TANO_AURA } from '../../utils/tanoSkins';
import robotUrl from '../../assets/robot-2x.webp';
import chapeauUrl from '../../assets/chapeau.webp';
import lunettesUrl from '../../assets/lunettes.webp';
import echarpeUrl from '../../assets/echarpe.webp';
import tuittuitUrl from '../../assets/tuittuit.webp';
import couronneUrl from '../../assets/couronne.webp';
import reacteurGaucheUrl from '../../assets/reacteur-gauche.webp';
import reacteurDroitUrl from '../../assets/reacteur-droit.webp';
/**
 * Mascotte ROBOT — une seule image webp + overlays SVG (plan mascotte robot).
 *
 * Le visage du robot est un ÉCRAN : les 5 moods sont DESSINÉS sur la visière
 * (masque sombre + yeux/bouche lumineux), nets à toute taille et assortis au
 * plumage. Les accessoires et auras sont des calques SVG (formes héritées du
 * moteur tuit-tuit, repositionnées). Le plumage est un filtre CSS sur l'image
 * (voir TANO_ROBOT_FILTRES). Si l'image ne charge pas, le parent bascule sur
 * le moteur SVG legacy via onErreur.
 *
 * ⚠ ANCRES : toutes les coordonnées (visière, chapeaux, écharpe) sont
 * calibrées sur `public/tano/robot@2x.webp` (robot centré, cadrage carré).
 * Si l'asset change de cadrage, ajuster ANCRES uniquement.
 */

// L'asset vit dans src/assets (bundlé par webpack, versionné avec le code).
const SOURCES = [robotUrl];

// Calibrage sur robot-2x.webp : image PORTRAIT 542×896 à visière VIERGE
// (l'écran est éteint, le visage overlay l'« allume »). En object-contain
// dans le carré 200×200, l'image occupe x ≈ 40..160 pleine hauteur.
const ANCRES = {
    visiere: { x: 60, y: 20, largeur: 81, hauteur: 62, arrondi: 26 },
    yeux: { gauche: 82, droit: 118, cy: 44, rayon: 9 },
    bouche: { cy: 66 },
    // Écharpe en IMAGE détourée (assets/echarpe.webp, ratio 800×507) —
    // la boucle au cou (jonction tête/torse ~y 84), le pan sur le torse
    echarpeImage: { x: 58, y: 84, largeur: 84, hauteur: 53 },
    // Lunettes en IMAGE détourée (assets/lunettes.webp, ratio 800×366) —
    // centrées sur les yeux de la visière (cy 44)
    lunettesImage: { x: 54, y: 25, largeur: 92, hauteur: 42 },
    // Chapeau créole en IMAGE détourée (assets/chapeau.webp, ratio 800×318) —
    // posé en travers du dôme, la calotte déborde au-dessus du cadre.
    // Largeur ≈ le dôme + débord du bord de paille de chaque côté.
    chapeauImage: { x: 34, y: -12, largeur: 132, hauteur: 52 },
    // Tuit-tuit compagnon (assets/tuittuit.webp, ~carré) — perché au sommet
    // du dôme, les pattes posées vers y 6 (id API `casque` conservé)
    tuitTuitImage: { x: 82, y: -30, largeur: 36, hauteur: 36 },
    // Couronne + cape royale (assets/couronne.webp, calque PLEIN CADRE
    // couvrant tête→sol) : rect calculé par alignement des silhouettes
    // (scène 1792×2400, robot y 187-2396 ↔ image contain x 40..160 pleine
    // hauteur). L'occultation par le corps est DANS l'alpha du calque — ne
    // pas le recadrer, déplacer le rect entier si besoin.
    couronneImage: { x: 19, y: -17, largeur: 162, hauteur: 214 },
    // Flammes de réacteur (assets/reacteur-gauche/droit.webp, ratio ~0.66) —
    // ⚠ coordonnées du CADRE (aura, hors inset 8 %) : elles jaillissent sous
    // les pieds (x 83/117, y 172), mêmes gabarits que les ex-flammes SVG
    reacteursImage: { gauche: { x: 72 }, droit: { x: 106 }, y: 172, largeur: 22, hauteur: 33 },
    // Jeu de néons sur l'armure (cœur torse, oreilles, épaules, genoux, pieds)
    neons: {
        coeur: { cx: 100, cy: 122, r: 7 },
        oreilles: { y: 56, gauche: 44, droit: 156, r: 4 },
        epaules: { y: 108, gauche: 62, droit: 138, r: 3 },
        genoux: { y: 162, gauche: 84, droit: 116, largeur: 12 },
        pieds: { y: 186, gauche: 80, droit: 120, largeur: 16 },
    },
};

// ── Visage-écran par mood (couleur = lueur du plumage) ──────────────────────

function Visage({ mood, lueur }) {
    const { visiere, yeux, bouche } = ANCRES;
    const oeil = (cx, contenu) => contenu(cx);
    const paire = (contenu) => (
        <>
            {oeil(yeux.gauche, contenu)}
            {oeil(yeux.droit, contenu)}
        </>
    );
    // Yeux « tuit-tuit » (recette du moteur legacy, tanoEngine) : blanc de
    // l'oeil + pupille sombre décalée + reflet — l'héritage de l'ancienne
    // mascotte sur l'écran du robot. Utilisés par idle/sad/angry ; joy garde
    // ses arcs de bonheur et sleep ses paupières, comme dans le moteur.
    const oeilTuitTuit = (cx, dy = 0, pupille = 4) => (
        <g key={cx}>
            <ellipse cx={cx} cy={yeux.cy + dy} rx="9" ry="7.5" fill="#FFFFFF" />
            <circle cx={cx + 1} cy={yeux.cy + dy + 1.5} r={pupille} fill="#101820" />
            <circle cx={cx + 3} cy={yeux.cy + dy - 2} r="1.8" fill="#FFFFFF" />
        </g>
    );
    let face;
    if (mood === 'joy') {
        face = (
            <>
                {paire((cx) => (
                    <path
                        key={cx}
                        d={`M${cx - 9},${yeux.cy + 2} Q${cx},${yeux.cy - 10} ${cx + 9},${yeux.cy + 2}`}
                        fill="none" stroke={lueur} strokeWidth="4" strokeLinecap="round"
                    />
                ))}
                <path
                    d={`M88,${bouche.cy - 4} Q100,${bouche.cy + 10} 112,${bouche.cy - 4}`}
                    fill="none" stroke={lueur} strokeWidth="3.5" strokeLinecap="round"
                />
                <circle cx={yeux.gauche - 14} cy={bouche.cy - 6} r="3" fill={lueur} opacity=".45" />
                <circle cx={yeux.droit + 14} cy={bouche.cy - 6} r="3" fill={lueur} opacity=".45" />
            </>
        );
    } else if (mood === 'sad') {
        face = (
            <>
                {paire((cx) => oeilTuitTuit(cx, 3, 3.5))}
                <path d={`M${yeux.gauche - 10},${yeux.cy - 9} L${yeux.gauche + 6},${yeux.cy - 13}`} stroke={lueur} strokeWidth="2.5" strokeLinecap="round" />
                <path d={`M${yeux.droit + 10},${yeux.cy - 9} L${yeux.droit - 6},${yeux.cy - 13}`} stroke={lueur} strokeWidth="2.5" strokeLinecap="round" />
                <path
                    d={`M92,${bouche.cy + 3} Q100,${bouche.cy - 4} 108,${bouche.cy + 3}`}
                    fill="none" stroke={lueur} strokeWidth="3" strokeLinecap="round"
                />
                <path className="tano-tear" d={`M${yeux.gauche - 2},${yeux.cy + 11} Q${yeux.gauche - 5},${yeux.cy + 18} ${yeux.gauche - 2},${yeux.cy + 21} Q${yeux.gauche + 1},${yeux.cy + 18} ${yeux.gauche - 2},${yeux.cy + 11} Z`} fill={lueur} />
            </>
        );
    } else if (mood === 'angry') {
        face = (
            <>
                {paire((cx) => oeilTuitTuit(cx, 1, 3.5))}
                <path d={`M${yeux.gauche - 11},${yeux.cy - 13} L${yeux.gauche + 7},${yeux.cy - 8}`} stroke={lueur} strokeWidth="3.5" strokeLinecap="round" />
                <path d={`M${yeux.droit + 11},${yeux.cy - 13} L${yeux.droit - 7},${yeux.cy - 8}`} stroke={lueur} strokeWidth="3.5" strokeLinecap="round" />
                <path d={`M93,${bouche.cy} L107,${bouche.cy}`} stroke={lueur} strokeWidth="3" strokeLinecap="round" />
            </>
        );
    } else if (mood === 'sleep') {
        face = (
            <>
                {paire((cx) => (
                    <path key={cx} d={`M${cx - 8},${yeux.cy} H${cx + 8}`} stroke={lueur} strokeWidth="3.5" strokeLinecap="round" />
                ))}
                <path
                    d={`M95,${bouche.cy} Q100,${bouche.cy + 5} 105,${bouche.cy}`}
                    fill="none" stroke={lueur} strokeWidth="2.5" strokeLinecap="round" opacity=".8"
                />
                {/* zZz qui s'échappent du sommeil, aux couleurs du skin */}
                <text x="128" y="30" fontSize="13" fontWeight="bold" fill={lueur} className="tano-pulse">z</text>
                <text x="140" y="16" fontSize="18" fontWeight="bold" fill={lueur} className="tano-pulse" style={{ animationDelay: '.6s' }}>Z</text>
                <text x="154" y="2" fontSize="24" fontWeight="bold" fill={lueur} className="tano-pulse" style={{ animationDelay: '1.2s' }}>z</text>
            </>
        );
    } else {
        // idle : yeux tuit-tuit à fond blanc (retour user 2026-08-11 —
        // remplace le cercle plein couleur lueur)
        face = (
            <>
                {paire((cx) => oeilTuitTuit(cx))}
                <path
                    d={`M94,${bouche.cy - 2} Q100,${bouche.cy + 4} 106,${bouche.cy - 2}`}
                    fill="none" stroke={lueur} strokeWidth="3" strokeLinecap="round"
                />
            </>
        );
    }
    return (
        <g style={{ filter: `drop-shadow(0 0 3px ${lueur})` }}>
            <rect
                x={visiere.x} y={visiere.y} width={visiere.largeur} height={visiere.hauteur}
                rx={visiere.arrondi} fill="#0A1220"
            />
            {face}
        </g>
    );
}

// ── Néons d'armure (torse, oreilles, épaules, genoux, pieds) ────────────────
// L'armure blanche se « colorie » par ces accents lumineux au plumage — on ne
// peut pas recolorer des zones d'un raster, on les souligne.

function Neons({ lueur }) {
    const n = ANCRES.neons;
    const barre = (cx, y, largeur) => (
        <path
            d={`M${cx - largeur / 2},${y} H${cx + largeur / 2}`}
            stroke={lueur} strokeWidth="3" strokeLinecap="round" opacity=".85"
        />
    );
    return (
        <g className="tano-pulse" style={{ filter: `drop-shadow(0 0 5px ${lueur})` }}>
            {/* cœur du torse : anneau + point */}
            <circle cx={n.coeur.cx} cy={n.coeur.cy} r={n.coeur.r} fill="none" stroke={lueur} strokeWidth="2.5" opacity=".9" />
            <circle cx={n.coeur.cx} cy={n.coeur.cy} r={n.coeur.r - 4} fill={lueur} opacity=".8" />
            {/* oreilles (pods latéraux de la tête) */}
            <circle cx={n.oreilles.gauche} cy={n.oreilles.y} r={n.oreilles.r} fill={lueur} opacity=".75" />
            <circle cx={n.oreilles.droit} cy={n.oreilles.y} r={n.oreilles.r} fill={lueur} opacity=".75" />
            {/* épaules */}
            <circle cx={n.epaules.gauche} cy={n.epaules.y} r={n.epaules.r} fill={lueur} opacity=".7" />
            <circle cx={n.epaules.droit} cy={n.epaules.y} r={n.epaules.r} fill={lueur} opacity=".7" />
            {/* genoux et pieds : barres lumineuses */}
            {barre(n.genoux.gauche, n.genoux.y, n.genoux.largeur)}
            {barre(n.genoux.droit, n.genoux.y, n.genoux.largeur)}
            {barre(n.pieds.gauche, n.pieds.y, n.pieds.largeur)}
            {barre(n.pieds.droit, n.pieds.y, n.pieds.largeur)}
        </g>
    );
}

// ── Accessoires (formes du moteur tuit-tuit, ré-ancrées sur le robot) ───────

function Accessoire({ acc }) {
    const opts = acc ? TANO_ACCESSOIRE[acc] : null;
    if (!opts) return null;
    return (
        <>
            {/* Chapeaux REDESSINÉS en coordonnées robot (posés sur le sommet du
                dôme ~y 10, la calotte déborde au-dessus du cadre : overflow
                visible). Les formes du tuit-tuit étaient pensées pour une
                petite tête ronde à y 35 — inadaptables par simple transform. */}
            {opts.hat === 'paille' ? (
                <image
                    href={chapeauUrl}
                    x={ANCRES.chapeauImage.x}
                    y={ANCRES.chapeauImage.y}
                    width={ANCRES.chapeauImage.largeur}
                    height={ANCRES.chapeauImage.hauteur}
                />
            ) : null}
            {opts.hat === 'couronne' ? (
                // Set royal complet (couronne + cape d'hermine) en un seul
                // calque plein cadre — l'alpha porte déjà l'occultation par
                // le corps, le rect se déplace en bloc (voir ANCRES)
                <image
                    href={couronneUrl}
                    x={ANCRES.couronneImage.x}
                    y={ANCRES.couronneImage.y}
                    width={ANCRES.couronneImage.largeur}
                    height={ANCRES.couronneImage.hauteur}
                />
            ) : null}
            {opts.hat === 'casque' ? (
                // Le tuit-tuit compagnon (l'ancienne mascotte perchée sur la
                // nouvelle) — l'id API `casque` est un contrat figé, seul le
                // label change (xpConfig : « Tuit-tuit »)
                <image
                    href={tuittuitUrl}
                    x={ANCRES.tuitTuitImage.x}
                    y={ANCRES.tuitTuitImage.y}
                    width={ANCRES.tuitTuitImage.largeur}
                    height={ANCRES.tuitTuitImage.hauteur}
                />
            ) : null}
            {opts.glasses ? (
                <image
                    href={lunettesUrl}
                    x={ANCRES.lunettesImage.x}
                    y={ANCRES.lunettesImage.y}
                    width={ANCRES.lunettesImage.largeur}
                    height={ANCRES.lunettesImage.hauteur}
                />
            ) : null}
            {opts.scarf ? (
                <image
                    href={echarpeUrl}
                    x={ANCRES.echarpeImage.x}
                    y={ANCRES.echarpeImage.y}
                    width={ANCRES.echarpeImage.largeur}
                    height={ANCRES.echarpeImage.hauteur}
                />
            ) : null}
        </>
    );
}

// ── Auras (reprises du moteur, autour de l'image) ───────────────────────────

function Aura({ aura }) {
    const opts = aura ? TANO_AURA[aura] : null;
    if (!opts) return null;
    return (
        <>
            {opts.sparkle ? (
                <>
                    <circle className="tano-etincelle" cx="40" cy="60" r="2.4" fill="#fff" />
                    <circle className="tano-etincelle" style={{ animationDelay: '.4s' }} cx="160" cy="80" r="2.8" fill="#FFB938" />
                    <circle className="tano-etincelle" style={{ animationDelay: '.8s' }} cx="150" cy="40" r="1.8" fill="#fff" />
                    <circle className="tano-etincelle" style={{ animationDelay: '1.2s' }} cx="52" cy="120" r="1.8" fill="#fff" />
                </>
            ) : null}
            {opts.flammeAura ? (
                <>
                    <path className="tano-flamme" d="M52,150 Q40,128 52,112 Q58,130 64,134 Q54,142 58,158 Z" fill="#F97316" />
                    <path className="tano-flamme" style={{ animationDelay: '.3s' }} d="M148,150 Q160,128 148,112 Q142,130 136,134 Q146,142 142,158 Z" fill="#F97316" />
                    <path className="tano-flamme" style={{ animationDelay: '.55s' }} d="M58,148 Q52,134 59,124 Q61,134 64,138 Z" fill="#FFB938" />
                    <path className="tano-flamme" style={{ animationDelay: '.15s' }} d="M142,148 Q148,134 141,124 Q139,134 136,138 Z" fill="#FFB938" />
                </>
            ) : null}
            {opts.haloArdent ? (
                <>
                    <circle cx="100" cy="104" r="90" fill="#F97316" opacity=".12" />
                    <circle className="tano-pulse" cx="100" cy="104" r="90" fill="none" stroke="#FF7A1A" strokeWidth="4" opacity=".65" />
                    <circle className="tano-pulse" cx="100" cy="104" r="80" fill="none" stroke="#FFB938" strokeWidth="2" opacity=".55" />
                </>
            ) : null}
            {opts.ring ? (
                <>
                    <circle cx="100" cy="104" r="92" fill="#F97316" opacity=".1" />
                    <g className="tano-spin" style={{ transformOrigin: '100px 104px' }}>
                        <circle cx="100" cy="104" r="92" fill="none" stroke="#FF7A1A" strokeWidth="6" strokeDasharray="14 9" strokeLinecap="round" />
                        <path d="M100,12 Q94,22 100,30 Q106,22 100,12 Z" fill="#FFB938" />
                        <path d="M192,104 Q182,98 174,104 Q182,110 192,104 Z" fill="#FFD27A" />
                        <path d="M100,196 Q106,186 100,178 Q94,186 100,196 Z" fill="#FF7A1A" />
                        <path d="M8,104 Q18,110 26,104 Q18,98 8,104 Z" fill="#FFB938" />
                    </g>
                </>
            ) : null}
            {opts.braiseHat ? (
                // Réacteurs : flammes IMAGES détourées sous les pieds (assets
                // reacteur-gauche/droit.webp), animées par la même classe que
                // les ex-flammes SVG (tano-flamme-bas : scaleY origine haut +
                // skew) — délais DÉCALÉS pour désynchroniser le flicker.
                // Coordonnées du CADRE : pieds du corps (inset 8 %) x 83/117, y 172.
                <g style={{ filter: 'drop-shadow(0 0 4px #FF7A1A)' }}>
                    <image
                        className="tano-flamme-bas"
                        href={reacteurGaucheUrl}
                        x={ANCRES.reacteursImage.gauche.x}
                        y={ANCRES.reacteursImage.y}
                        width={ANCRES.reacteursImage.largeur}
                        height={ANCRES.reacteursImage.hauteur}
                    />
                    <image
                        className="tano-flamme-bas"
                        style={{ animationDelay: '.35s' }}
                        href={reacteurDroitUrl}
                        x={ANCRES.reacteursImage.droit.x}
                        y={ANCRES.reacteursImage.y}
                        width={ANCRES.reacteursImage.largeur}
                        height={ANCRES.reacteursImage.hauteur}
                    />
                </g>
            ) : null}
        </>
    );
}

/**
 * @param {string} mood - 'idle' | 'joy' | 'sad' | 'angry' | 'sleep'
 * @param {string} [plume] / [acc] / [aura] - ids de skin (API)
 * @param {Function} onErreur - appelée si AUCUNE source d'image ne charge
 */
const TanoRobot = memo(function TanoRobot({ mood = 'idle', plume, acc, aura, onErreur }) {
    const [idxSource, setIdxSource] = useState(0);
    const { filtre, lueur, lueurVisage } = TANO_ROBOT_FILTRES[plume] || TANO_ROBOT_FILTRES.foret;

    const surErreur = () => {
        if (idxSource + 1 < SOURCES.length) setIdxSource(idxSource + 1);
        else if (onErreur) onErreur();
    };

    return (
        <div className="relative w-full h-full">
            {/* L'aura ne respire pas : calque séparé, stable */}
            {/* overflow-visible : les flammes de réacteur dépassent le bas du cadre */}
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full overflow-visible">
                <Aura aura={aura} />
            </svg>
            {/* Le corps respire (tanoIdle) — image + visage + néons + accessoires.
                Le retrait de 8 % laisse la place aux chapeaux et met les skins
                en valeur (l'aura, elle, occupe tout le cadre). */}
            <div className={`tano-corps absolute inset-0${mood === 'sleep' ? ' tano-corps-sommeil' : ''}`}>
                <div className="absolute inset-[8%]">
                    <img
                        src={SOURCES[idxSource]}
                        alt=""
                        onError={surErreur}
                        className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
                        style={filtre !== 'none' ? { filter: filtre } : undefined}
                        draggable={false}
                    />
                    <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full overflow-visible">
                        <Neons lueur={lueur} />
                        <Visage mood={mood} lueur={lueurVisage || lueur} />
                        <Accessoire acc={acc} />
                    </svg>
                </div>
            </div>
        </div>
    );
});

export default TanoRobot;
